// Non-processor buildings: Crate (plank box + count label), Drill (hazard base; the head and bit are
// overlays), the Gallery booth (awning, 3-target rail, gun stand, 4 ammo-queue dots), the v0.2 Gunsmith and
// Dock, and deposits (a rock outcrop with veins of the ore; crystals grow with the level).

import { GALLERY } from '../anchors.ts';
import type { Ctx2D } from '../ctx.ts';
import { fillP, type Part } from '../passes.ts';
import { mulberry32 } from '../rng.ts';
import { blobPts, circle, ellipse, line, poly, rrect, smoothPoly } from '../shapes.ts';
import { type MachKit, plaque, strokeP } from './body.ts';

export function crateParts(m: MachKit): Part[] {
  const wood = m.sw('machine.crate');
  const w = m.x1 - m.x0;
  const top = m.rect('machine.crate', m.x0, m.y0, w, m.yf - m.y0 + 9, [9, 9, 0, 0], {
    shade: false,
    detail: (c) => {
      for (const y of [m.y0 + 18, m.y0 + 36, m.y0 + 54])
        strokeP(
          c,
          line([
            [m.x0 + 3, y],
            [m.x1 - 3, y],
          ]),
          wood.shade,
          m.ow * 0.45,
        );
      strokeP(
        c,
        line([
          [m.x0 + 8, m.y0 + 8],
          [m.x1 - 8, m.yf - 4],
        ]),
        wood.outline,
        m.ow * 0.5,
      );
      fillP(c, rrect(32, 28, 36, 22, 4), m.pal.hex('ui.cream2'));
      fillP(c, rrect(32, 28, 36, 5, 2), m.pal.hex(m.pal.tier(m.level)));
    },
  });
  const front = m.rect('ui.woodDark', m.x0, m.yf, w, m.y1 - m.yf, [0, 0, 9, 9], {
    shade: false,
    hi: false,
    detail: (c) => plaque(m, c, 50),
  });
  return [top, front];
}

export function drillBaseParts(m: MachKit): Part[] {
  const ink = m.pal.hex('ui.ink');
  return [
    m.rect('mach.front', 24, 18, 52, 50, 10, {
      detail: (c) => {
        // hazard stripes around the turntable
        c.save();
        c.beginPath();
        rrect(24, 18, 52, 50, 10)(c);
        c.clip();
        c.fillStyle = m.pal.hex(m.fn);
        c.fillRect(24, 18, 52, 50);
        c.fillStyle = ink;
        for (let i = -4; i < 8; i++) {
          c.beginPath();
          poly([
            [24 + i * 12, 68],
            [30 + i * 12, 68],
            [54 + i * 12, 18],
            [48 + i * 12, 18],
          ])(c);
          c.fill();
        }
        c.restore();
        fillP(c, circle(50, 43, 16), m.sw('mach.top').shade);
        fillP(c, circle(50, 43, 12), m.pal.hex('mach.top'));
      },
    }),
  ];
}

/** The drill head overlay (points N; rotated toward the deposit at runtime). */
export function drillHeadParts(m: MachKit): Part[] {
  const fn = m.sw(m.fn);
  const ink = m.pal.hex('ui.ink');
  return [
    // steel chuck toward the deposit (N); the spinning bit (mp/drill/bit) sits on its tip
    m.poly(
      'material.steel',
      [
        [41, 38],
        [59, 38],
        [55, 16],
        [45, 16],
      ],
      2,
    ),
    // motor housing with vents and level lamps
    m.rect(m.fn, 30, 36, 40, 30, 9, {
      detail: (c) => {
        for (const x of [38, 44, 50, 56, 62]) fillP(c, rrect(x - 1.2, 41, 2.4, 10, 1.2), fn.shade);
        fillP(c, rrect(30, 55, 40, 3, 0), ink);
        for (let i = 0; i < 4; i++) {
          fillP(c, circle(41 + i * 6, 61, 2), i < m.level ? m.pal.hex('ui.money') : fn.shade);
        }
      },
    }),
  ];
}

export function galleryParts(m: MachKit): Part[] {
  const cream = m.pal.hex('ui.cream');
  const wood = m.sw('ui.wood');
  const parts: Part[] = [];
  // back wall
  parts.push(
    m.rect('ui.woodDark', m.x0 + 2, 20, m.x1 - m.x0 - 4, 38, 4, {
      shade: false,
      detail: (c) => {
        if (m.level >= 3) {
          for (let i = 0; i < 6; i++)
            fillP(c, circle(16 + i * 13.6, 24, 1.8), m.pal.hex('glow.merge'));
        }
      },
    }),
  );
  // awning with cream stripes and scalloped edge
  parts.push({
    ...m.rect('machine.gallery', m.x0, m.y0, m.x1 - m.x0, 18, [8, 8, 2, 2], { shade: false }),
    path: (c) => {
      const x0 = m.x0;
      const x1 = m.x1;
      c.moveTo(x0 + 8, m.y0);
      c.lineTo(x1 - 8, m.y0);
      c.arcTo(x1, m.y0, x1, m.y0 + 8, 8);
      c.lineTo(x1, m.y0 + 14);
      const n = 6;
      const sw = (x1 - x0) / n;
      for (let i = n; i > 0; i--) {
        const xa = x0 + i * sw;
        c.arc(xa - sw / 2, m.y0 + 14, sw / 2, 0, Math.PI);
      }
      c.lineTo(x0, m.y0 + 8);
      c.arcTo(x0, m.y0, x0 + 8, m.y0, 8);
      c.closePath();
    },
    detail: (c) => {
      c.save();
      c.beginPath();
      rrect(m.x0, m.y0, m.x1 - m.x0, 26, 8)(c);
      c.clip();
      const n = 6;
      const sw = (m.x1 - m.x0) / n;
      for (let i = 0; i < n; i += 2) {
        c.fillStyle = cream;
        c.fillRect(m.x0 + (i + 1) * sw, m.y0, sw, 26);
      }
      c.restore();
    },
  });
  // target rail with 3 pegs
  parts.push(
    m.rect('ui.wood', m.x0 + 4, 50, m.x1 - m.x0 - 8, 8, 3, {
      detail: (c) => {
        for (const px of GALLERY.pegX)
          fillP(c, rrect(px * 100 - 3, GALLERY.railY * 100, 6, 5, 1.5), wood.shade);
      },
    }),
  );
  // counter (top face front) with the gun stand
  parts.push(
    m.rect('ui.wood', m.x0, 58, m.x1 - m.x0, 20, [4, 4, 0, 0], {
      shade: false,
      detail: (c) => {
        fillP(c, rrect(44, 60, 12, 8, 2), m.pal.hex('mach.front'));
        fillP(c, rrect(47, 56, 6, 6, 1.5), m.pal.hex('mach.top'));
      },
    }),
  );
  parts.push(
    m.rect('machine.gallery', m.x0, 74, m.x1 - m.x0, m.y1 - 74, [0, 0, 9, 9], {
      shade: false,
      hi: false,
      detail: (c) => {
        for (let i = 0; i < GALLERY.queueDots; i++) {
          const q = GALLERY.queueDot;
          fillP(
            c,
            circle((q.x + i * q.dx) * 100, q.y * 100, q.r * 100),
            m.sw('machine.gallery').outline,
          );
        }
        plaque(m, c, 72);
        if (m.level >= 2) {
          for (const x of [m.x0 + 3, m.x1 - 3])
            fillP(c, circle(x, m.y0 + 18, 2.4), m.pal.hex('ui.money'));
        }
      },
    }),
  );
  if (m.level >= 4)
    parts.push(
      m.poly(
        'tier.7',
        [
          [44, m.y0 + 3],
          [50, m.y0 + 0.5],
          [56, m.y0 + 3],
          [53, m.y0 + 10],
          [47, m.y0 + 10],
        ],
        1,
      ),
    );
  return parts;
}

export function gunsmithParts(m: MachKit): Part[] {
  const wood = m.sw('ui.wood');
  return [
    m.rect('ui.wood', 12, 18, 76, 40, 5, {
      detail: (c) => {
        for (const y of [30, 44])
          strokeP(
            c,
            line([
              [14, y],
              [86, y],
            ]),
            wood.shade,
            m.ow * 0.4,
          );
        fillP(c, rrect(20, 24, 34, 24, 2), m.pal.hex('glow.select'));
        strokeP(
          c,
          line([
            [24, 40],
            [44, 32],
            [50, 34],
          ]),
          m.sw('glow.select').outline,
          m.ow * 0.5,
        );
      },
    }),
    m.rect('mach.front', 60, 22, 22, 16, 3),
    m.rect(m.fn, 64, 36, 14, 10, 2),
  ];
}

export function dockParts(m: MachKit): Part[] {
  return [
    m.rect('ui.wood', 10, 40, 80, 28, 4),
    m.rect('ui.buyDark', 16, 18, 28, 26, 4, {
      detail: (c) => fillP(c, rrect(16, 28, 28, 5, 0), m.pal.hex(m.pal.tier(m.level))),
    }),
    m.rect('machine.crate', 48, 22, 22, 22, 3),
    m.rect(m.fn, 70, 30, 18, 20, 4),
  ];
}

const ORE_TOK: Record<string, [string, string]> = {
  lead: ['material.lead', 'material.steel'],
  coal: ['material.coal', 'material.coalGlint'],
  copper: ['material.copper', 'material.copperPatina'],
  iron: ['material.iron', 'material.steel'],
};

/** Deposit: rock outcrop, ore veins, crystals by level (1 → 4 clusters), level plaque. */
export function depositParts(m: MachKit, ore: string, seed: number): Part[] {
  const [bodyTok, fleckTok] = ORE_TOK[ore] ?? ['material.lead', 'material.steel'];
  const rng = mulberry32(seed);
  const rock = m.sw('ui.inkSoft');
  const ore1 = m.sw(bodyTok);
  const fl = m.sw(fleckTok);
  const mound = blobPts(rng, 50, 46, 42, 32, 11, 0.07, 0.2);
  const parts: Part[] = [];
  parts.push({
    ...m.rect('ui.inkSoft', 8, 14, 84, 62, 0),
    path: smoothPoly(mound),
    detail: (c: Ctx2D) => {
      // ore veins
      for (let i = 0; i < 3 + m.level; i++) {
        const cx = 22 + rng() * 56;
        const cy = 26 + rng() * 34;
        const r = 5 + rng() * 5;
        fillP(c, ellipse(cx, cy, r * 1.4, r, rng() * 3), ore1.base);
        fillP(c, ellipse(cx - r * 0.4, cy - r * 0.35, r * 0.5, r * 0.3, 0), ore1.hi);
      }
      for (let i = 0; i < 4; i++) {
        fillP(c, circle(20 + rng() * 60, 24 + rng() * 40, 1.6 + rng() * 1.4), rock.shade);
      }
    },
  });
  // crystals: one cluster per level
  // (the first cluster's tip + outline stays inside the frame: no upward overhang in v0.1)
  const spots: [number, number][] = [
    [50, 25],
    [26, 30],
    [74, 30],
    [50, 38],
  ];
  for (let i = 0; i < m.level; i++) {
    const [cx, cy] = spots[i] ?? [50, 30];
    const h = 14 + (i === 0 ? 4 : 0);
    parts.push(
      m.poly(
        fleckTok,
        [
          [cx - 6, cy + 6],
          [cx - 5, cy - h + 6],
          [cx, cy - h],
          [cx + 5, cy - h + 6],
          [cx + 6, cy + 6],
        ],
        1.2,
        {
          detail: (c) =>
            strokeP(
              c,
              line([
                [cx, cy - h + 2],
                [cx - 1, cy + 4],
              ]),
              fl.light,
              m.ow * 0.45,
            ),
        },
      ),
    );
  }
  parts.push(
    m.rect('ui.woodDark', m.x0, m.yf, m.x1 - m.x0, m.y1 - m.yf, [3, 3, 9, 9], {
      shade: false,
      hi: false,
      detail: (c) => {
        fillP(c, rrect(m.x0, m.yf, m.x1 - m.x0, 3.2, 0), ore1.base);
        plaque(m, c, 50);
      },
    }),
  );
  return parts;
}
